import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { ConstantsService } from '../../constants/constants.service';
import { HttpService } from '../../Services/http.service';

@Component({
  selector: 'app-complete-profile-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './complete-profile-modal.component.html',
  styleUrl: './complete-profile-modal.component.scss'
})
export class CompleteProfileModalComponent implements OnInit, OnDestroy {
  showModal: boolean = false;
  isSubmitting: boolean = false;
  currentUser: any = null;

  profileForm = {
    fullName: '',
    designation: ''
  };

  profileErrors = {
    fullName: '',
    designation: ''
  };

  private routerSub?: Subscription;
  private readonly allowedRoles = ['ADMIN', 'DISTRICT_ADMIN', 'TALUKA_ADMIN'];

  constructor(
    private router: Router,
    private constants: ConstantsService,
    private httpService: HttpService
  ) {}

  ngOnInit(): void {
    this.checkProfileStatus();
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkProfileStatus();
      });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  checkProfileStatus(): void {
    const storage = this.constants.getSessionStorage();
    if (!storage || !storage.getItem('currentUser')) {
      this.showModal = false;
      this.currentUser = null;
      return;
    }

    try {
      const user = JSON.parse(storage.getItem('currentUser') || '{}');
      const roleName = user?.role?.name;

      if (roleName && this.allowedRoles.includes(roleName)) {
        // If profile is not updated yet
        const isUpdated = user.profileUpdated === true || user.profileUpdated === 'true';
        if (!isUpdated) {
          this.currentUser = user;
          this.profileForm.fullName = user.fullName || '';
          this.profileForm.designation = user.designation || '';
          this.showModal = true;
          return;
        }
      }
    } catch (e) {
      console.error('Error checking user profile status', e);
    }

    this.showModal = false;
  }

  validate(): boolean {
    let isValid = true;
    this.profileErrors = { fullName: '', designation: '' };

    if (!this.profileForm.fullName || !this.profileForm.fullName.trim()) {
      this.profileErrors.fullName = 'पूर्ण नाव भरणे आवश्यक आहे.';
      isValid = false;
    }

    if (!this.profileForm.designation || !this.profileForm.designation.trim()) {
      this.profileErrors.designation = 'पद / हुद्दा भरणे आवश्यक आहे.';
      isValid = false;
    }

    return isValid;
  }

  submitProfile(): void {
    if (!this.validate()) {
      return;
    }

    this.isSubmitting = true;
    const payload = {
      fullName: this.profileForm.fullName.trim(),
      designation: this.profileForm.designation.trim()
    };

    this.httpService.putRequest(this.constants.SERVER_URLS['COMPLETE_PROFILE'], payload).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        if (res?.status === this.constants.RESPONSE_CODES['SUCCESS']) {
          this.constants.successMsg(res.message || 'प्रोफाइल माहिती यशस्वीरित्या जतन केली आहे.');

          // Update currentUser in sessionStorage
          const storage = this.constants.getSessionStorage();
          if (storage && storage.getItem('currentUser')) {
            const user = JSON.parse(storage.getItem('currentUser') || '{}');
            const existingToken = user.sessionToken; // Preserve existing JWT token
            user.fullName = payload.fullName;
            user.designation = payload.designation;
            user.profileUpdated = true;
            if (res.result && typeof res.result === 'object') {
              Object.assign(user, res.result);
            }
            // Ensure sessionToken is never lost:
            user.sessionToken = existingToken;
            user.profileUpdated = true;
            storage.setItem('currentUser', JSON.stringify(user));
            this.currentUser = user;
          }

          this.showModal = false;
        } else {
          this.constants.errorMsg(res?.message || 'माहिती जतन करताना त्रुटी आली.');
        }
      },
      error: (err: any) => {
        this.isSubmitting = false;
        const msg = err?.error?.message || err?.message || 'माहिती जतन करताना त्रुटी आली.';
        this.constants.errorMsg(msg);
      }
    });
  }

  closeModal(): void {
    if (this.currentUser?.profileUpdated) {
      this.showModal = false;
    }
  }
}
